import {
  Button,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MatrixNode,
  MatrixNodeRequest,
  matrixConfigurationApi,
} from '@texas/api/endpoints/matrixConfigurationApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';

export function MatrixNodeModal({
  canEdit,
  isOpen,
  node,
  onClose,
  onSaved,
}: {
  canEdit: boolean;
  isOpen: boolean;
  node: MatrixNode;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState<MatrixNodeRequest>(() => toRequest(node));
  const {
    request: update,
    loading,
    error,
  } = useApiRequest(matrixConfigurationApi.updateNode);

  useEffect(() => {
    setForm(toRequest(node));
  }, [isOpen, node]);

  const save = async (request = form) => {
    const response = await update(node.id, request);
    if (response.hasError) return;
    toast({
      title:
        request.archived !== node.archived
          ? t(
              request.archived
                ? 'general.entityArchived'
                : 'general.entityRestored',
              { name: request.name },
            )
          : t('general.updated'),
      status: 'success',
      isClosable: true,
    });
    onSaved();
    onClose();
  };

  const setNullableNumber = (
    field: 'valueMinLength' | 'valueMaxLength',
    value: string,
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value === '' ? null : Number(value),
      [field === 'valueMinLength' ? 'hasValueMinLength' : 'hasValueMaxLength']:
        value !== '',
    }));
  };

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{node.name}</ModalHeader>
        <ModalBody>
          <ErrorDetails error={error} />
          <Flex direction="column" gap="4">
            <FormControl isRequired={true}>
              <TexasFormLabel>{t('general.name')}</TexasFormLabel>
              <Input
                variant="entityDirectory"
                value={form.name}
                isDisabled={!canEdit}
                maxLength={256}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
              />
              <FormCounter length={form.name.length} maxLength={256} />
            </FormControl>
            <FormControl>
              <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
              <Input
                type="number"
                variant="entityDirectory"
                value={form.displayOrder}
                isDisabled={!canEdit}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    displayOrder: Number(event.target.value),
                  }))
                }
              />
            </FormControl>
            <Flex gap="4">
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.valueMinLength')}
                </TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  value={form.valueMinLength ?? ''}
                  isDisabled={!canEdit}
                  onChange={(event) =>
                    setNullableNumber('valueMinLength', event.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.valueMaxLength')}
                </TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  value={form.valueMaxLength ?? ''}
                  isDisabled={!canEdit}
                  onChange={(event) =>
                    setNullableNumber('valueMaxLength', event.target.value)
                  }
                />
              </FormControl>
            </Flex>
            <Table variant="entityDirectoryCompact" size="sm">
              <Thead>
                <Tr>
                  <Th>{t('general.name')}</Th>
                  <Th>{t('configuration.identifier')}</Th>
                  <Th>{t('branch.displayOrder')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {form.matrixNodeValues
                  .filter((value) => !value.archived)
                  .sort((left, right) => left.displayOrder - right.displayOrder)
                  .map((value) => (
                    <Tr key={value.id}>
                      <Td>
                        <Input
                          variant="entityDirectory"
                          size="sm"
                          value={value.name}
                          maxLength={20}
                          isDisabled={!canEdit}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              matrixNodeValues: current.matrixNodeValues.map(
                                (item) =>
                                  item.id === value.id
                                    ? { ...item, name: event.target.value }
                                    : item,
                              ),
                            }))
                          }
                        />
                        <FormCounter
                          length={value.name.length}
                          maxLength={20}
                        />
                      </Td>
                      <Td>{value.identifier}</Td>
                      <Td>{value.displayOrder}</Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </Flex>
        </ModalBody>
        <ModalFooter gap="2">
          {canEdit && !form.archived && (
            <Button
              variant="ghost"
              onClick={() => {
                const request = { ...form, archived: true };
                setForm(request);
                save(request);
              }}
            >
              {t('general.archive')}
            </Button>
          )}
          {canEdit && (
            <Button
              variant="entityDirectoryPrimary"
              isLoading={loading}
              onClick={() => save()}
            >
              {t('general.save')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function toRequest(node: MatrixNode): MatrixNodeRequest {
  return {
    name: node.name,
    valueMinLength: node.valueMinLength,
    valueMaxLength: node.valueMaxLength,
    displayOrder: node.displayOrder,
    allowedAxis: node.allowedAxis,
    archived: node.archived,
    hasValueMinLength: node.valueMinLength !== null,
    hasValueMaxLength: node.valueMaxLength !== null,
    matrixNodeValues: node.matrixNodeValues.map((value) => ({ ...value })),
  };
}
